import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import './PerformanceDashboard.css';

type ServerMetrics = {
  timestamp: number;
  cpuUsage: number;
  memoryUsage: number;
  loadAverage: number;
  eventLoopLag: number;
  activeConnections: number;
  responseTime: number;
  errorRate: number;
  requestsPerMinute: number;
  workerCount?: number;
  cpuUsageMax?: number;
  cpuUsageMin?: number;
  memoryUsageMax?: number;
  memoryUsageMin?: number;
  loadAverageMax?: number;
  eventLoopLagMax?: number;
  responseTimeMax?: number;
  perCpuUsage?: number[];
};

type CpuLevel = {
  cpuUsage: number;
  max: number;
  perUserLimit: number;
  label: string;
};

type WorkerMetrics = ServerMetrics & {
  workerId: string;
  instanceId?: string | null;
  pmId?: string | null;
  pid: number;
  hostname: string;
  transport?: string;
  uptimeSeconds?: number;
  startedAt?: number | null;
  lastUpdated: number;
  isLocal?: boolean;
};

type MetricsResponse = {
  server: ServerMetrics;
  workers?: {
    total: number;
    items: WorkerMetrics[];
  };
  cpuLevel: CpuLevel;
  anomalies: {
    total: number;
    critical: number;
    warning: number;
    items: Anomaly[];
  };
  throttles: Array<{ username: string; level: string; expiresAt: number | null }>;
  timestamp: string;
};

type HistoryEntry = {
  timestamp: number;
  cpuUsage: number;
  memoryUsage: number;
  loadAverage: number;
  responseTime: number;
  errorRate: number;
  requestsPerMinute: number;
  activeConnections: number;
};

type Anomaly = {
  username: string;
  status: string;
  anomalyScore: number;
  metrics: {
    requestsPerMinute: number;
    sseConnections: number;
    avgResponseTime: number;
    rateLimitHits: number;
  };
  lastUpdated: number;
};

type AnomalyResponse = {
  total: number;
  anomalies: Anomaly[];
  baseline: {
    requestsPerMinute: number;
    sseConnections: number;
    avgResponseTime: number;
  };
  timestamp: string;
};

// API Call Tracking Types
type ApiCall = {
  timestamp: number;
  method: string;
  path: string;
  endpoint: string;
  statusCode: number;
  duration: number;
  ip: string;
  username: string | null;
  isAuthenticated: boolean;
  userAgent: string;
  isSuspicious: boolean;
  suspiciousReason?: string;
};

type ApiCallStats = {
  totalCalls: number;
  recentCalls: number;
  unauthenticatedCalls: number;
  errorCalls: number;
  suspiciousCalls: number;
  uniqueIPs: number;
  uniqueUsers: number;
  timestamp: string;
};

type ApiCallsResponse = {
  total: number;
  calls: ApiCall[];
  timestamp: string;
};

type EndpointStat = {
  endpoint: string;
  count: number;
  errors: number;
  errorRate: string;
  avgResponseTime: string;
  methods: string[];
  lastCall: number | null;
};

type EndpointsResponse = {
  total: number;
  endpoints: EndpointStat[];
  timestamp: string;
};

type CpuThresholdConfig = {
  max: number | null;
  perUserLimit: number;
  label: string;
};

type ThrottlingConfig = {
  enabled: boolean;
  cpuThresholds: CpuThresholdConfig[];
  minPerUserLimit: number;
  maxPerUserLimit: number;
};

type ThrottlingConfigResponse = {
  success: boolean;
  config: ThrottlingConfig;
  timestamp: string;
};

type HubHealthCyclePoint = {
  label: string;
  cpuPercentAvg: number | null;
  memoryPercentAvg: number | null;
  sampleCount: number;
};

type HubHealthHistoryPayload = {
  success: boolean;
  history?: {
    last24h: Array<{ ts: string; cpuPercent: number | null; memoryPercent: number | null }>;
    dailyCycle: HubHealthCyclePoint[];
    weeklyCycle: HubHealthCyclePoint[];
    monthlyCycle: HubHealthCyclePoint[];
  };
};

const HISTORY_REFRESH_INTERVAL = 60 * 1000;
const METRIC_REFRESH_INTERVAL = 5000;

const formatTime = (timestamp: number) => new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const safeNumber = (value: unknown, fallback = 0) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const formatDuration = (seconds?: number) => {
  if (!seconds || !Number.isFinite(seconds)) {
    return '—';
  }
  const totalSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const PerformanceDashboard = () => {
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [anomalies, setAnomalies] = useState<AnomalyResponse | null>(null);
  const [apiCallStats, setApiCallStats] = useState<ApiCallStats | null>(null);
  const [suspiciousCalls, setSuspiciousCalls] = useState<ApiCallsResponse | null>(null);
  const [unauthenticatedCalls, setUnauthenticatedCalls] = useState<ApiCallsResponse | null>(null);
  const [endpointStats, setEndpointStats] = useState<EndpointsResponse | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'suspicious' | 'unauthenticated' | 'endpoints'>('overview');
  const [isLive, setIsLive] = useState(true);
  const [timeRange, setTimeRange] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [throttlingConfig, setThrottlingConfig] = useState<ThrottlingConfig | null>(null);
  const [showWorkerDetails, setShowWorkerDetails] = useState(false);
  const [hubHealthRange, setHubHealthRange] = useState<'all' | '90d' | '30d'>('all');
  const [hubHealth, setHubHealth] = useState<HubHealthHistoryPayload['history'] | null>(null);
  const [hubHealthLoading, setHubHealthLoading] = useState(false);
  const [hubHealthError, setHubHealthError] = useState<string | null>(null);
  const [throttlingDraft, setThrottlingDraft] = useState<ThrottlingConfig | null>(null);
  const [throttlingLoading, setThrottlingLoading] = useState(true);
  const [throttlingError, setThrottlingError] = useState<string | null>(null);
  const [throttlingSuccess, setThrottlingSuccess] = useState<string | null>(null);
  const [isSavingThrottling, setIsSavingThrottling] = useState(false);

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await fetch('/api/performance/metrics', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: MetricsResponse = await response.json();
      setMetrics(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch performance metrics', err);
      setError('Performance-Metriken konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async (range: number) => {
    try {
      const response = await fetch(`/api/performance/history?hours=${range}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setHistory(data.data || []);
    } catch (err) {
      console.error('Failed to fetch performance history', err);
    }
  }, []);

  const fetchAnomalies = useCallback(async () => {
    try {
      const response = await fetch('/api/performance/anomalies', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: AnomalyResponse = await response.json();
      setAnomalies(data);
    } catch (err) {
      console.error('Failed to fetch anomalies', err);
    }
  }, []);

  const fetchApiCallStats = useCallback(async () => {
    try {
      const response = await fetch('/api/performance/api-calls/stats', {
        credentials: 'include'
      });
      if (response.ok) {
        const data: ApiCallStats = await response.json();
        setApiCallStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch API call stats', err);
    }
  }, []);

  const fetchSuspiciousCalls = useCallback(async () => {
    try {
      const response = await fetch('/api/performance/api-calls/suspicious?limit=50', {
        credentials: 'include'
      });
      if (response.ok) {
        const data: ApiCallsResponse = await response.json();
        setSuspiciousCalls(data);
      }
    } catch (err) {
      console.error('Failed to fetch suspicious calls', err);
    }
  }, []);

  const fetchUnauthenticatedCalls = useCallback(async () => {
    try {
      const response = await fetch('/api/performance/api-calls/unauthenticated?limit=100', {
        credentials: 'include'
      });
      if (response.ok) {
        const data: ApiCallsResponse = await response.json();
        setUnauthenticatedCalls(data);
      }
    } catch (err) {
      console.error('Failed to fetch unauthenticated calls', err);
    }
  }, []);

  const fetchEndpointStats = useCallback(async () => {
    try {
      const response = await fetch('/api/performance/api-calls/endpoints?limit=50', {
        credentials: 'include'
      });
      if (response.ok) {
        const data: EndpointsResponse = await response.json();
        setEndpointStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch endpoint stats', err);
    }
  }, []);

  const fetchThrottlingConfig = useCallback(async () => {
    try {
      setThrottlingLoading(true);
      const response = await fetch('/api/performance/throttling/config', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: ThrottlingConfigResponse = await response.json();
      setThrottlingConfig(data.config);
      setThrottlingDraft(data.config);
      setThrottlingError(null);
    } catch (err) {
      console.error('Failed to fetch throttling config', err);
      setThrottlingError('Throttling-Konfiguration konnte nicht geladen werden');
    } finally {
      setThrottlingLoading(false);
    }
  }, []);

  const fetchHubHealth = useCallback(async (range: 'all' | '90d' | '30d') => {
    try {
      setHubHealthLoading(true);
      const response = await fetch(`/api/hub/health/history?range=${encodeURIComponent(range)}`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data: HubHealthHistoryPayload = await response.json();
      setHubHealth(data.history || null);
      setHubHealthError(null);
    } catch (err) {
      console.error('Failed to fetch hub health history', err);
      setHubHealth(null);
      setHubHealthError('HUB-Auslastungshistorie konnte nicht geladen werden');
    } finally {
      setHubHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    fetchHistory(timeRange);
    fetchAnomalies();
    fetchApiCallStats();
    fetchSuspiciousCalls();
    fetchUnauthenticatedCalls();
    fetchEndpointStats();
    fetchThrottlingConfig();
    fetchHubHealth(hubHealthRange);
  }, [fetchMetrics, fetchHistory, fetchAnomalies, fetchApiCallStats, fetchSuspiciousCalls, fetchUnauthenticatedCalls, fetchEndpointStats, fetchThrottlingConfig, fetchHubHealth, timeRange, hubHealthRange]);

  useEffect(() => {
    if (!isLive) {
      return;
    }

    const metricInterval = window.setInterval(() => {
      fetchMetrics();
      fetchAnomalies();
      fetchApiCallStats();
      if (activeTab === 'suspicious') fetchSuspiciousCalls();
      if (activeTab === 'unauthenticated') fetchUnauthenticatedCalls();
      if (activeTab === 'endpoints') fetchEndpointStats();
    }, METRIC_REFRESH_INTERVAL);

    const historyInterval = window.setInterval(() => {
      fetchHistory(timeRange);
    }, HISTORY_REFRESH_INTERVAL);

    return () => {
      window.clearInterval(metricInterval);
      window.clearInterval(historyInterval);
    };
  }, [fetchMetrics, fetchAnomalies, fetchHistory, fetchApiCallStats, fetchSuspiciousCalls, fetchUnauthenticatedCalls, fetchEndpointStats, isLive, timeRange, activeTab]);

  useEffect(() => {
    if (!throttlingSuccess) {
      return;
    }
    const timeout = window.setTimeout(() => setThrottlingSuccess(null), 4000);
    return () => window.clearTimeout(timeout);
  }, [throttlingSuccess]);

  const historyWithLabels = useMemo(
    () =>
      history.map((entry) => ({
        ...entry,
        timeLabel: formatTime(entry.timestamp)
      })),
    [history]
  );

  const statusCards = useMemo(() => {
    if (!metrics) {
      return [];
    }

    const server = metrics.server ?? {
      cpuUsage: 0,
      memoryUsage: 0,
      responseTime: 0,
      requestsPerMinute: 0
    } as Partial<ServerMetrics>;

    const cpuUsage = safeNumber(server.cpuUsage);
    const memoryUsage = safeNumber(server.memoryUsage);
    const responseTime = safeNumber(server.responseTime);
    const requestsPerMinute = safeNumber(server.requestsPerMinute);
    const workerCount = metrics.workers?.total ?? safeNumber(server.workerCount, 1);

    return [
      {
        title: 'Worker aktiv',
        value: `${workerCount}`,
        status: workerCount > 1 ? 'normal' : 'warning'
      },
      {
        title: 'CPU-Auslastung',
        value: `${cpuUsage.toFixed(1)}%`,
        status: cpuUsage > 85 ? 'critical' : cpuUsage > 70 ? 'warning' : 'normal'
      },
      {
        title: 'RAM-Auslastung',
        value: `${memoryUsage.toFixed(1)}%`,
        status: memoryUsage > 90 ? 'critical' : memoryUsage > 75 ? 'warning' : 'normal'
      },
      {
        title: 'Antwortzeit',
        value: `${responseTime.toFixed(0)} ms`,
        status: responseTime > 5000 ? 'critical' : responseTime > 2000 ? 'warning' : 'normal'
      },
      {
        title: 'Requests/Minute',
        value: requestsPerMinute.toFixed(1),
        status: 'normal'
      }
    ];
  }, [metrics]);

  const workerMetrics = useMemo(() => {
    if (!metrics?.workers?.items) {
      return [] as WorkerMetrics[];
    }
    return [...metrics.workers.items].sort((a, b) => a.workerId.localeCompare(b.workerId));
  }, [metrics]);

  const workerSummary = useMemo(() => {
    if (!workerMetrics.length) {
      return null;
    }

    const aggregate = workerMetrics.reduce(
      (acc, worker) => {
        const cpu = safeNumber(worker.cpuUsage);
        const ram = safeNumber(worker.memoryUsage);
        const req = safeNumber(worker.requestsPerMinute);
        const err = safeNumber(worker.errorRate);
        const connections = safeNumber(worker.activeConnections);
        const uptime = worker.uptimeSeconds ?? 0;

        acc.totalCpu += cpu;
        acc.totalRam += ram;
        acc.totalReq += req;
        acc.totalErr += err;
        acc.totalConnections += connections;
        acc.totalUptime += uptime;

        if (cpu > acc.maxCpuValue) {
          acc.maxCpuValue = cpu;
          acc.maxCpuWorker = worker;
        }

        if (ram > acc.maxRamValue) {
          acc.maxRamValue = ram;
          acc.maxRamWorker = worker;
        }

        if (req > acc.maxReqValue) {
          acc.maxReqValue = req;
          acc.maxReqWorker = worker;
        }

        return acc;
      },
      {
        totalCpu: 0,
        totalRam: 0,
        totalReq: 0,
        totalErr: 0,
        totalConnections: 0,
        totalUptime: 0,
        maxCpuWorker: workerMetrics[0],
        maxCpuValue: safeNumber(workerMetrics[0].cpuUsage),
        maxRamWorker: workerMetrics[0],
        maxRamValue: safeNumber(workerMetrics[0].memoryUsage),
        maxReqWorker: workerMetrics[0],
        maxReqValue: safeNumber(workerMetrics[0].requestsPerMinute)
      }
    );

    const count = workerMetrics.length;
    const avgCpu = aggregate.totalCpu / count;
    const avgRam = aggregate.totalRam / count;
    const avgUptimeMinutes = aggregate.totalUptime / count / 60;

    return {
      count,
      avgCpu,
      avgRam,
      totalReq: aggregate.totalReq,
      totalErr: aggregate.totalErr,
      totalConnections: aggregate.totalConnections,
      avgUptimeMinutes,
      busiestWorker: aggregate.maxReqWorker,
      hottestWorker: aggregate.maxCpuWorker,
      memoryHeavyWorker: aggregate.maxRamWorker
    };
  }, [workerMetrics]);

  const throttlingDirty = useMemo(() => {
    if (!throttlingDraft && !throttlingConfig) {
      return false;
    }
    return JSON.stringify(throttlingDraft) !== JSON.stringify(throttlingConfig);
  }, [throttlingDraft, throttlingConfig]);

  const handleToggleThrottling = useCallback(() => {
    setThrottlingSuccess(null);
    setThrottlingDraft((prev) => (prev ? { ...prev, enabled: !prev.enabled } : prev));
  }, []);

  const handleLimitChange = useCallback((field: 'maxPerUserLimit' | 'minPerUserLimit', value: string) => {
    const parsed = Number(value);
    setThrottlingSuccess(null);
    setThrottlingDraft((prev) => {
      if (!prev) {
        return prev;
      }
      const nextValue = Number.isFinite(parsed) ? Math.max(1, Math.round(parsed)) : prev[field];
      const updated = { ...prev, [field]: nextValue };
      if (field === 'minPerUserLimit' && updated.minPerUserLimit > updated.maxPerUserLimit) {
        updated.maxPerUserLimit = updated.minPerUserLimit;
      }
      if (field === 'maxPerUserLimit' && updated.maxPerUserLimit < updated.minPerUserLimit) {
        updated.minPerUserLimit = updated.maxPerUserLimit;
      }
      return updated;
    });
  }, []);

  const handleThresholdChange = useCallback((index: number, field: 'max' | 'perUserLimit', value: string) => {
    setThrottlingSuccess(null);
    setThrottlingDraft((prev) => {
      if (!prev) {
        return prev;
      }
      const thresholds = prev.cpuThresholds.map((entry, idx) => {
        if (idx !== index) {
          return entry;
        }
        if (field === 'max') {
          if (value === '') {
            return { ...entry, max: null };
          }
          const parsed = Number(value);
          const bounded = Number.isFinite(parsed) ? Math.max(1, Math.min(100, Math.round(parsed))) : entry.max;
          return {
            ...entry,
            max: bounded
          };
        }
        const parsed = Number(value);
        return {
          ...entry,
          perUserLimit: Number.isFinite(parsed) ? Math.max(1, Math.round(parsed)) : entry.perUserLimit
        };
      });
      return {
        ...prev,
        cpuThresholds: thresholds
      };
    });
  }, []);

  const handleSaveThrottling = useCallback(async () => {
    if (!throttlingDraft) {
      return;
    }
    setIsSavingThrottling(true);
    setThrottlingError(null);
    setThrottlingSuccess(null);
    try {
      const payload = {
        enabled: throttlingDraft.enabled,
        cpuThresholds: throttlingDraft.cpuThresholds.map((entry) => ({
          label: entry.label,
          perUserLimit: entry.perUserLimit,
          max: entry.max
        })),
        minPerUserLimit: throttlingDraft.minPerUserLimit,
        maxPerUserLimit: throttlingDraft.maxPerUserLimit
      };
      const response = await fetch('/api/performance/throttling/config', {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.message || `HTTP ${response.status}`);
      }
      const data: ThrottlingConfigResponse = await response.json();
      setThrottlingConfig(data.config);
      setThrottlingDraft(data.config);
      setThrottlingSuccess('Throttling-Einstellungen gespeichert');
    } catch (err) {
      console.error('Failed to update throttling config', err);
      setThrottlingError(err instanceof Error ? err.message : 'Unbekannter Fehler beim Speichern');
    } finally {
      setIsSavingThrottling(false);
    }
  }, [throttlingDraft]);

  const latestWorkerUpdate = useMemo(() => {
    if (!workerMetrics.length) {
      return Date.now();
    }
    return workerMetrics.reduce((max, worker) => Math.max(max, worker.lastUpdated ?? 0), 0);
  }, [workerMetrics]);

  const clusterCpuCoreData = useMemo(() => {
    const perCore = metrics?.server?.perCpuUsage;
    if (!Array.isArray(perCore) || perCore.length === 0) {
      return [] as { core: string; usage: number }[];
    }

    return perCore.map((value, index) => {
      const usage = safeNumber(value);
      return {
        core: `CPU ${index + 1}`,
        usage: Number(usage.toFixed(1))
      };
    });
  }, [metrics]);

  const hubLast24hChart = useMemo(
    () =>
      (hubHealth?.last24h || []).map((row) => ({
        label: new Date(row.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        cpuPercentAvg: row.cpuPercent,
        memoryPercentAvg: row.memoryPercent
      })),
    [hubHealth]
  );

  return (
    <div className="performance-dashboard">
      <div className="dashboard-header">
        <div>
          <h1>🚀 Lernrakete Performance Monitor</h1>
          <p className="subtitle">Live-Einblick in Systemauslastung, Rate Limiting und Anomalien</p>
        </div>
        <div className="controls">
          <button className={`live-toggle ${isLive ? 'active' : ''}`} onClick={() => setIsLive((prev) => !prev)}>
            {isLive ? '⏸️ Live stoppen' : '▶️ Live starten'}
          </button>
          <select
            className="time-range-selector"
            value={timeRange}
            onChange={(event) => setTimeRange(Number(event.target.value))}
          >
            <option value={1}>Letzte Stunde</option>
            <option value={6}>Letzte 6 Stunden</option>
            <option value={12}>Letzte 12 Stunden</option>
            <option value={24}>Letzte 24 Stunden</option>
          </select>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="status-cards">
        {statusCards.map((card) => (
          <div className={`status-card ${card.status}`} key={card.title}>
            <p className="status-title">{card.title}</p>
            <p className="status-value">{card.value}</p>
          </div>
        ))}
      </div>

      {clusterCpuCoreData.length > 0 && (
        <div className="cpu-core-section">
          <div className="cpu-core-header">
            <h3>🧠 CPU-Auslastung pro Kern</h3>
            <span>Cluster-Durchschnitt · {clusterCpuCoreData.length} Kerne</span>
          </div>
          <div className="cpu-core-chart">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={clusterCpuCoreData} margin={{ top: 10, left: 0, right: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2c3353" />
                <XAxis dataKey="core" tick={{ fill: '#ccc', fontSize: 12 }} />
                <YAxis domain={[0, 100]} tick={{ fill: '#ccc', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ background: '#1f1f2d', border: 'none', borderRadius: '8px', color: '#fff' }}
                  labelStyle={{ color: '#aaa' }}
                />
                <Bar dataKey="usage" fill="#60a5fa" name="Auslastung %" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="hub-health-section">
        <div className="hub-health-header">
          <h3>🩺 HUB Health-Historie</h3>
          <select
            className="time-range-selector"
            value={hubHealthRange}
            onChange={(event) => setHubHealthRange(event.target.value as 'all' | '90d' | '30d')}
          >
            <option value="all">Mittelwerte aus allen Daten</option>
            <option value="90d">Mittelwerte aus letzten 90 Tagen</option>
            <option value="30d">Mittelwerte aus letzten 30 Tagen</option>
          </select>
        </div>
        {hubHealthError && <div className="error-banner small">{hubHealthError}</div>}
        {hubHealthLoading ? (
          <p className="empty-state">Lade HUB-Auslastung…</p>
        ) : (
          <div className="charts-grid">
            <HubHealthChart title="Letzte 24 Stunden" data={hubLast24hChart} />
            <HubHealthChart title="Gemittelte Tagesganglinie" data={hubHealth?.dailyCycle || []} />
            <HubHealthChart title="Wochenganglinie" data={hubHealth?.weeklyCycle || []} />
            <HubHealthChart title="Monatsganglinie" data={hubHealth?.monthlyCycle || []} />
          </div>
        )}
      </div>

      {workerMetrics.length > 0 && workerSummary && (
        <div className="worker-section">
          <div className="worker-header">
            <h3>👷 Worker-Status</h3>
            <div className="worker-meta">
              <span>{workerSummary.count} aktive Worker</span>
              <span>Letztes Update {formatTime(latestWorkerUpdate)}</span>
              <button
                type="button"
                className="worker-toggle"
                onClick={() => setShowWorkerDetails((prev) => !prev)}
              >
                {showWorkerDetails ? 'Details ausblenden' : 'Details anzeigen'}
              </button>
            </div>
          </div>
          <div className="worker-summary-grid">
            <div className="worker-summary-card">
              <p className="summary-label">Ø CPU-Auslastung</p>
              <h4>{workerSummary.avgCpu.toFixed(1)}%</h4>
              <span>Höchster Wert: {safeNumber(workerSummary.hottestWorker?.cpuUsage).toFixed(1)}% ({workerSummary.hottestWorker?.workerId})</span>
            </div>
            <div className="worker-summary-card">
              <p className="summary-label">Ø RAM-Auslastung</p>
              <h4>{workerSummary.avgRam.toFixed(1)}%</h4>
              <span>Peak bei {safeNumber(workerSummary.memoryHeavyWorker?.memoryUsage).toFixed(1)}% ({workerSummary.memoryHeavyWorker?.workerId})</span>
            </div>
            <div className="worker-summary-card">
              <p className="summary-label">Traffic gesamt</p>
              <h4>{workerSummary.totalReq.toFixed(1)} req/min</h4>
              <span>Busiester Worker: {workerSummary.busiestWorker?.workerId} ({safeNumber(workerSummary.busiestWorker?.requestsPerMinute).toFixed(1)} req/min)</span>
            </div>
            <div className="worker-summary-card">
              <p className="summary-label">Verbindungen aktiv</p>
              <h4>{workerSummary.totalConnections.toFixed(0)}</h4>
              <span>Ø Laufzeit: {workerSummary.avgUptimeMinutes.toFixed(1)} Minuten</span>
            </div>
          </div>
          {showWorkerDetails && (
            <div className="worker-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Worker</th>
                    <th>Hostname</th>
                    <th>PID</th>
                    <th>Instanz</th>
                    <th>Laufzeit</th>
                    <th>CPU%</th>
                    <th>CPU-Kerne</th>
                    <th>RAM%</th>
                    <th>Requests/min</th>
                    <th>Fehler/min</th>
                    <th>Verbindungen</th>
                    <th>Stand</th>
                  </tr>
                </thead>
                <tbody>
                  {workerMetrics.map((worker) => {
                    const isStale = Date.now() - worker.lastUpdated > 20000;
                    return (
                      <tr
                        key={worker.workerId}
                        className={`worker-row ${worker.isLocal ? 'local' : ''} ${isStale ? 'stale' : ''}`}
                      >
                        <td>
                          <div className="worker-id">
                            {worker.workerId}
                            {worker.isLocal && <span className="worker-badge">local</span>}
                          </div>
                        </td>
                        <td>{worker.hostname}</td>
                        <td>{worker.pid}</td>
                        <td>{worker.instanceId ?? worker.pmId ?? '—'}</td>
                        <td>{formatDuration(worker.uptimeSeconds)}</td>
                        <td>{safeNumber(worker.cpuUsage).toFixed(1)}%</td>
                        <td>
                          {Array.isArray(worker.perCpuUsage) && worker.perCpuUsage.length > 0 ? (
                            <div className="cpu-core-list">
                              {worker.perCpuUsage.map((value, index) => {
                                const usage = safeNumber(value);
                                const status = usage > 85 ? 'critical' : usage > 70 ? 'warning' : 'normal';
                                return (
                                  <span key={`${worker.workerId}-core-${index}`} className={`cpu-core-chip ${status}`}>
                                    {index + 1}: {usage.toFixed(0)}%
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>{safeNumber(worker.memoryUsage).toFixed(1)}%</td>
                        <td>{safeNumber(worker.requestsPerMinute).toFixed(1)}</td>
                        <td>{safeNumber(worker.errorRate).toFixed(1)}</td>
                        <td>{safeNumber(worker.activeConnections).toFixed(0)}</td>
                        <td>{formatTime(worker.lastUpdated)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="charts-grid">
        <div className="chart-card">
          <div className="chart-header">
            <h3>🖥️ CPU & RAM</h3>
            <span>Aktuelle Trends</span>
          </div>
          <div className="chart-body">
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={historyWithLabels} margin={{ top: 10, left: 0, right: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorCpu" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorRam" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="timeLabel" interval="preserveStartEnd" tick={{ fill: '#ccc', fontSize: 12 }} />
                <YAxis domain={[0, 100]} tick={{ fill: '#ccc', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ background: '#1f1f2d', border: 'none', borderRadius: '8px', color: '#fff' }}
                  labelStyle={{ color: '#aaa' }}
                />
                <Area type="monotone" dataKey="cpuUsage" stroke="#8884d8" fillOpacity={1} fill="url(#colorCpu)" name="CPU %" />
                <Area type="monotone" dataKey="memoryUsage" stroke="#82ca9d" fillOpacity={1} fill="url(#colorRam)" name="RAM %" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>⚡ Antwortzeit & Fehler</h3>
            <span>Stabilität im Blick</span>
          </div>
          <div className="chart-body">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={historyWithLabels} margin={{ top: 10, left: 0, right: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="timeLabel" tick={{ fill: '#ccc', fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fill: '#ccc', fontSize: 12 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: '#ccc', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ background: '#1f1f2d', border: 'none', borderRadius: '8px', color: '#fff' }}
                  labelStyle={{ color: '#aaa' }}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="responseTime"
                  stroke="#ff6b6b"
                  strokeWidth={2}
                  dot={false}
                  name="Antwortzeit (ms)"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="errorRate"
                  stroke="#ffa726"
                  strokeWidth={2}
                  dot={false}
                  name="Fehler / Minute"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>📈 Request-Volumen</h3>
            <span>Rate Limiting im Blick</span>
          </div>
          <div className="chart-body">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={historyWithLabels} margin={{ top: 10, left: 0, right: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="timeLabel" tick={{ fill: '#ccc', fontSize: 12 }} />
                <YAxis tick={{ fill: '#ccc', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ background: '#1f1f2d', border: 'none', borderRadius: '8px', color: '#fff' }}
                  labelStyle={{ color: '#aaa' }}
                />
                <Bar dataKey="requestsPerMinute" fill="#4fc3f7" name="Requests / Minute" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="throttling-config-section">
        <div className="throttling-config-header">
          <div>
            <h3>🛡️ Adaptive Throttling</h3>
            <p className="throttling-subtitle">
              Steuert das serverseitige Rate-Limiting in Abhängigkeit von CPU-Last und Nutzersignalen.
            </p>
          </div>
          <div className="throttling-config-actions">
            <button
              type="button"
              className={`throttling-toggle ${throttlingDraft?.enabled ? 'active' : ''}`}
              onClick={handleToggleThrottling}
              disabled={!throttlingDraft}
            >
              {throttlingDraft?.enabled ? 'Throttling aktiv' : 'Throttling deaktiviert'}
            </button>
            <button
              type="button"
              className="throttling-save"
              onClick={handleSaveThrottling}
              disabled={!throttlingDraft || isSavingThrottling || !throttlingDirty}
            >
              {isSavingThrottling ? 'Speichere…' : throttlingDirty ? 'Änderungen speichern' : 'Gespeichert'}
            </button>
          </div>
        </div>
        {throttlingError && <div className="error-banner small">{throttlingError}</div>}
        {throttlingSuccess && <div className="success-banner">{throttlingSuccess}</div>}
        {throttlingLoading ? (
          <p className="empty-state">Lade Throttling-Konfiguration…</p>
        ) : throttlingDraft ? (
          <>
            <div className="throttling-grid">
              <div className="throttling-limits">
                <label>
                  <span>Max. Requests pro Benutzer (1 Minute)</span>
                  <input
                    type="number"
                    min={1}
                    className="throttling-input"
                    value={throttlingDraft.maxPerUserLimit}
                    onChange={(event) => handleLimitChange('maxPerUserLimit', event.target.value)}
                  />
                </label>
                <label>
                  <span>Min. Requests pro Benutzer (1 Minute)</span>
                  <input
                    type="number"
                    min={1}
                    className="throttling-input"
                    value={throttlingDraft.minPerUserLimit}
                    onChange={(event) => handleLimitChange('minPerUserLimit', event.target.value)}
                  />
                </label>
              </div>
              <div className="throttling-table-wrapper">
                <table className="throttling-table">
                  <thead>
                    <tr>
                      <th>CPU-Level</th>
                      <th>bis CPU %</th>
                      <th>Limit (Requests/min)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {throttlingDraft.cpuThresholds.map((entry, index) => (
                      <tr key={`${entry.label}-${index}`}>
                        <td>{entry.label}</td>
                        <td>
                          {entry.max === null ? (
                            <span className="infinity-label">∞</span>
                          ) : (
                            <input
                              type="number"
                              min={1}
                              max={100}
                              className="throttling-input"
                              value={entry.max}
                              onChange={(event) => handleThresholdChange(index, 'max', event.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            min={1}
                            className="throttling-input"
                            value={entry.perUserLimit}
                            onChange={(event) => handleThresholdChange(index, 'perUserLimit', event.target.value)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="throttling-footnote">
              Hinweis: Die Schwellenwerte werden von niedriger zu höherer CPU-Auslastung ausgewertet. Die letzte Zeile gilt für alle Werte darüber.
            </p>
          </>
        ) : (
          <p className="empty-state">Keine Konfiguration verfügbar.</p>
        )}
      </div>

      <div className="anomaly-section">
        <div className="anomaly-header">
          <h3>🚨 Anomalie-Überwachung</h3>
          <span>
            {anomalies?.total ?? 0} auffällige Nutzer · Baseline: {safeNumber(anomalies?.baseline.requestsPerMinute).toFixed(1)} Requests/min
          </span>
        </div>
        <div className="anomaly-table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Nutzer</th>
                <th>Status</th>
                <th>Requests/min</th>
                <th>SSE-Verbindungen</th>
                <th>Antwortzeit</th>
                <th>Score</th>
                <th>Letzte Aktivität</th>
              </tr>
            </thead>
            <tbody>
              {(metrics?.anomalies?.items ?? []).map((item) => (
                <tr key={item.username} className={`anomaly-row ${item.status.toLowerCase()}`}>
                  <td>{item.username}</td>
                  <td>
                    <span className={`badge ${item.status.toLowerCase()}`}>{item.status}</span>
                  </td>
                  <td>{safeNumber(item.metrics.requestsPerMinute).toFixed(1)}</td>
                  <td>{item.metrics.sseConnections ?? 0}</td>
                  <td>{safeNumber(item.metrics.avgResponseTime).toFixed(0)} ms</td>
                  <td>{safeNumber(item.anomalyScore).toFixed(1)}</td>
                  <td>{formatTime(item.lastUpdated ?? Date.now())}</td>
                </tr>
              ))}
              {metrics?.anomalies?.items && metrics.anomalies.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty-state">
                    🎉 Keine Anomalien erkannt – das System läuft stabil.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="throttle-section">
        <h3>🛡️ Aktive Throttles</h3>
        <div className="throttle-list">
          {metrics?.throttles?.length ? (
            metrics.throttles.map((entry) => (
              <div key={`${entry.username}-${entry.level}`} className="throttle-card">
                <p className="throttle-user">{entry.username}</p>
                <p className="throttle-level">Level: {entry.level}</p>
                <p className="throttle-expiry">
                  {entry.expiresAt ? `Läuft ab: ${new Date(entry.expiresAt).toLocaleTimeString()}` : 'Manuell aktiv'}
                </p>
              </div>
            ))
          ) : (
            <p className="empty-state">Keine manuellen Drosselungen aktiv.</p>
          )}
        </div>
      </div>

      <div className="api-calls-section">
        <div className="api-calls-header">
          <h3>🔍 API-Call-Überwachung</h3>
          <div className="api-calls-stats">
            {apiCallStats && (
              <>
                <span>📊 {apiCallStats.totalCalls.toLocaleString()} Total</span>
                <span>⚡ {apiCallStats.recentCalls} Letzte Minute</span>
                <span className={apiCallStats.unauthenticatedCalls > 0 ? 'warning' : ''}>
                  🔓 {apiCallStats.unauthenticatedCalls} Unauthentifiziert
                </span>
                <span className={apiCallStats.suspiciousCalls > 0 ? 'critical' : ''}>
                  🚨 {apiCallStats.suspiciousCalls} Verdächtig
                </span>
                <span className={apiCallStats.errorCalls > 0 ? 'warning' : ''}>
                  ❌ {apiCallStats.errorCalls} Fehler
                </span>
              </>
            )}
          </div>
        </div>

        <div className="api-calls-tabs">
          <button
            className={activeTab === 'suspicious' ? 'active' : ''}
            onClick={() => setActiveTab('suspicious')}
          >
            🚨 Verdächtige Calls ({suspiciousCalls?.total ?? 0})
          </button>
          <button
            className={activeTab === 'unauthenticated' ? 'active' : ''}
            onClick={() => setActiveTab('unauthenticated')}
          >
            🔓 Unauthentifiziert ({unauthenticatedCalls?.total ?? 0})
          </button>
          <button
            className={activeTab === 'endpoints' ? 'active' : ''}
            onClick={() => setActiveTab('endpoints')}
          >
            📍 Endpoints ({endpointStats?.total ?? 0})
          </button>
        </div>

        {activeTab === 'suspicious' && (
          <div className="api-calls-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Zeit</th>
                  <th>Methode</th>
                  <th>Endpoint</th>
                  <th>Status</th>
                  <th>IP</th>
                  <th>User</th>
                  <th>Dauer</th>
                  <th>Grund</th>
                </tr>
              </thead>
              <tbody>
                {suspiciousCalls?.calls.map((call, idx) => (
                  <tr key={idx} className="suspicious-row">
                    <td>{formatTime(call.timestamp)}</td>
                    <td><span className={`method-badge ${call.method.toLowerCase()}`}>{call.method}</span></td>
                    <td className="endpoint-cell">{call.path}</td>
                    <td>
                      <span className={`status-badge status-${call.statusCode}`}>
                        {call.statusCode}
                      </span>
                    </td>
                    <td>{call.ip}</td>
                    <td>{call.username || <span className="unauth-badge">❌ Unauthentifiziert</span>}</td>
                    <td>{call.duration.toFixed(0)} ms</td>
                    <td className="suspicious-reason">{call.suspiciousReason || 'Verdächtig'}</td>
                  </tr>
                ))}
                {suspiciousCalls && suspiciousCalls.calls.length === 0 && (
                  <tr>
                    <td colSpan={8} className="empty-state">
                      ✅ Keine verdächtigen API-Calls erkannt
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'unauthenticated' && (
          <div className="api-calls-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Zeit</th>
                  <th>Methode</th>
                  <th>Endpoint</th>
                  <th>Status</th>
                  <th>IP</th>
                  <th>User-Agent</th>
                  <th>Dauer</th>
                </tr>
              </thead>
              <tbody>
                {unauthenticatedCalls?.calls.map((call, idx) => (
                  <tr key={idx} className={call.statusCode >= 400 ? 'error-row' : ''}>
                    <td>{formatTime(call.timestamp)}</td>
                    <td><span className={`method-badge ${call.method.toLowerCase()}`}>{call.method}</span></td>
                    <td className="endpoint-cell">{call.path}</td>
                    <td>
                      <span className={`status-badge status-${call.statusCode}`}>
                        {call.statusCode}
                      </span>
                    </td>
                    <td>{call.ip}</td>
                    <td className="user-agent-cell" title={call.userAgent}>
                      {call.userAgent.length > 50 ? call.userAgent.substring(0, 50) + '...' : call.userAgent}
                    </td>
                    <td>{call.duration.toFixed(0)} ms</td>
                  </tr>
                ))}
                {unauthenticatedCalls && unauthenticatedCalls.calls.length === 0 && (
                  <tr>
                    <td colSpan={7} className="empty-state">
                      ✅ Keine unauthentifizierten API-Calls
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'endpoints' && (
          <div className="api-calls-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Endpoint</th>
                  <th>Aufrufe</th>
                  <th>Fehler</th>
                  <th>Fehlerrate</th>
                  <th>Ø Antwortzeit</th>
                  <th>Methoden</th>
                  <th>Letzter Aufruf</th>
                </tr>
              </thead>
              <tbody>
                {endpointStats?.endpoints.map((stat, idx) => (
                  <tr key={idx} className={parseFloat(stat.errorRate) > 10 ? 'warning-row' : ''}>
                    <td className="endpoint-cell">{stat.endpoint}</td>
                    <td>{stat.count.toLocaleString()}</td>
                    <td>{stat.errors}</td>
                    <td>
                      <span className={parseFloat(stat.errorRate) > 10 ? 'error-rate-high' : ''}>
                        {stat.errorRate}%
                      </span>
                    </td>
                    <td>{stat.avgResponseTime} ms</td>
                    <td>{stat.methods.join(', ')}</td>
                    <td>{stat.lastCall ? formatTime(stat.lastCall) : '-'}</td>
                  </tr>
                ))}
                {endpointStats && endpointStats.endpoints.length === 0 && (
                  <tr>
                    <td colSpan={7} className="empty-state">
                      📊 Keine Endpoint-Statistiken verfügbar
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isLoading && <div className="loading-overlay">Lade Performance-Daten…</div>}
    </div>
  );
};

export default PerformanceDashboard;

function HubHealthChart({ title, data }: { title: string; data: Array<{ label: string; cpuPercentAvg: number | null; memoryPercentAvg: number | null }> }) {
  return (
    <div className="chart-card">
      <div className="chart-header">
        <h3>{title}</h3>
        <span>CPU / RAM in %</span>
      </div>
      <div className="chart-body">
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data} margin={{ top: 10, left: 0, right: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis dataKey="label" tick={{ fill: '#ccc', fontSize: 12 }} />
            <YAxis domain={[0, 100]} tick={{ fill: '#ccc', fontSize: 12 }} />
            <Tooltip
              contentStyle={{ background: '#1f1f2d', border: 'none', borderRadius: '8px', color: '#fff' }}
              labelStyle={{ color: '#aaa' }}
              formatter={(value: number) => `${value?.toFixed?.(1) ?? value}%`}
            />
            <Line type="monotone" dataKey="cpuPercentAvg" stroke="#2563eb" strokeWidth={2.2} dot={false} connectNulls name="CPU" />
            <Line type="monotone" dataKey="memoryPercentAvg" stroke="#dc2626" strokeWidth={2.2} dot={false} connectNulls name="RAM" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

